import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { promiseWithTimeout } from '../../lib/promiseWithTimeout.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import VinylCover from '../components/VinylCover.js';
import { DCard, BtnGold, Badge } from '../components/MarketplaceAtoms.js';
import { buyerConfirmTransactionReceipt } from '../../lib/transactionsService.js';
import { PARTNER_CUSTODY_STATUS_LABEL_ES } from '../constants/partnerCustody.js';
import { isPartnerHubTransaction, buyerDeliveryModeLabel } from '../constants/transactionLogistics.js';
import TransactionShipmentPanel from '../components/TransactionShipmentPanel.js';
import OrderListingMedia, { OrderStepProductThumbs } from '../components/OrderListingMedia.js';
import { listingGalleryUrls } from '../utils/listingGalleryUrls.js';
import { MARKETPLACE_SLA, slaOrderStepHint } from '../constants/marketplaceSla.js';

const FETCH_MS = 22000;

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function shippingLabel(type) {
  const t = String(type || '').toLowerCase();
  if (t.includes('pickup')) return 'Recogida';
  if (t.includes('verified') || t.includes('verific') || t.includes('tienda_verificadora')) {
    return 'Partner verificador';
  }
  if (t.includes('physical') || t.includes('meet')) return 'Entrega en mano';
  if (t.includes('direct') || t === '' || t.includes('ship')) return 'Envío';
  return type || 'Envío';
}

function isVerifierShippingType(type) {
  const t = String(type || '').toLowerCase();
  return t.includes('tienda_verificadora') || t.includes('verific');
}

function isPickupShippingType(type) {
  const t = String(type || '').toLowerCase();
  return t.includes('punto_recogida') || (t.includes('pickup') && !t.includes('verific'));
}

function buildStepsVerifier(tx, custody, partner) {
  const cancelled = String(tx.status || '').toLowerCase().includes('cancel');
  const confirmed = !!tx.buyer_confirmed_at;
  const st = custody?.status || 'esperando';
  const storeName = partner?.company_name || 'Tienda verificadora';
  const storeAddr = [partner?.address, partner?.city].filter(Boolean).join(', ');

  if (cancelled) {
    return [
      { label: 'Pedido cancelado', date: fmtDateTime(tx.created_at), desc: `Estado: ${tx.status || 'cancelado'}`, done: false, pend: true },
    ];
  }

  const order = ['esperando', 'recibido', 'en_revision', 'verificado', 'listo', 'entregado'];
  const idx = Math.max(0, order.indexOf(st));

  const steps = [
    {
      label: 'Compra confirmada',
      date: fmtDateTime(tx.created_at),
      desc: `Tienda elegida: ${storeName}${storeAddr ? ` · ${storeAddr}` : ''}. El vendedor debe llevar el disco.`,
      done: true,
    },
    {
      label: 'Esperando entrega del vendedor',
      date: idx >= 1 ? fmtDate(custody?.updated_at) : 'Pendiente',
      desc: `El vendedor debe entregar el disco en ${storeName} para iniciar la verificación.`,
      done: idx >= 1,
      act: idx === 0,
      eta: idx === 0 ? slaOrderStepHint(MARKETPLACE_SLA.sellerToPartner) : null,
      showProductImage: idx <= 1,
    },
    {
      label: 'En tienda · verificación',
      date: idx >= 2 ? fmtDate(custody?.updated_at) : 'Pendiente',
      desc:
        idx >= 4
          ? `Estado: ${PARTNER_CUSTODY_STATUS_LABEL_ES[st] || st}`
          : 'La tienda revisa el estado del disco una vez lo tiene en custodia.',
      done: idx >= 3,
      act: idx >= 1 && idx < 4,
      eta: idx >= 1 && idx < 4 ? slaOrderStepHint(MARKETPLACE_SLA.partnerStoreVerification) : null,
    },
    {
      label:
        tx.buyer_partner_delivery_mode === 'ship_to_buyer' ? 'Envío desde la tienda' : 'Listo para recoger',
      date: idx >= 4 ? fmtDate(custody?.updated_at) : 'Pendiente',
      desc:
        tx.buyer_partner_delivery_mode === 'ship_to_buyer'
          ? idx >= 4
            ? 'La tienda ha preparado el envío a tu dirección.'
            : `Cuando la verificación termine, la tienda enviará el paquete a la dirección indicada en el checkout.`
          : idx >= 4 && custody?.delivery_pin
            ? `Recógelo en ${storeName}. Código: ${custody.delivery_pin}`
            : `Te avisaremos cuando puedas recogerlo en ${storeName}.`,
      done: idx >= 4,
      act: idx === 4 && !confirmed,
      eta:
        idx === 4 && tx.buyer_partner_delivery_mode === 'ship_to_buyer' && !confirmed
          ? slaOrderStepHint(MARKETPLACE_SLA.partnerOutboundToBuyer)
          : null,
    },
    {
      label: 'Recepción confirmada',
      date: confirmed ? fmtDateTime(tx.buyer_confirmed_at) : 'Pendiente',
      desc: confirmed ? 'Has confirmado la recepción del disco.' : 'Confirma cuando lo tengas en tu poder.',
      done: confirmed,
      pend: !confirmed && idx >= 4,
    },
  ];
  return steps;
}

function buildStepsPickup(tx, pkg, partner) {
  const cancelled = String(tx.status || '').toLowerCase().includes('cancel');
  const confirmed = !!tx.buyer_confirmed_at;
  const st = pkg?.status || 'asignado';
  const pointName = partner?.company_name || 'Punto DreamVinyl';
  const pointAddr = [partner?.address, partner?.city].filter(Boolean).join(', ');
  const buyerShip = tx.buyer_partner_delivery_mode === 'ship_to_buyer';

  if (cancelled) {
    return [
      { label: 'Pedido cancelado', date: fmtDateTime(tx.created_at), desc: `Estado: ${tx.status || 'cancelado'}`, done: false, pend: true },
    ];
  }

  const order = ['asignado', 'recibido', 'entregado'];
  const idx = Math.max(0, order.indexOf(st));

  return [
    {
      label: 'Compra confirmada',
      date: fmtDateTime(tx.created_at),
      desc: `Punto elegido: ${pointName}${pointAddr ? ` · ${pointAddr}` : ''}.`,
      done: true,
    },
    {
      label: 'Esperando entrega del vendedor',
      date: idx >= 1 ? fmtDate(pkg?.updated_at) : 'Pendiente',
      desc: `El vendedor debe llevar el disco a ${pointName}.`,
      done: idx >= 1,
      act: idx === 0,
      eta: idx === 0 ? slaOrderStepHint(MARKETPLACE_SLA.sellerToPartner) : null,
      showProductImage: idx <= 1,
    },
    {
      label: 'En punto · recepción del paquete',
      date: idx >= 1 ? fmtDate(pkg?.updated_at) : 'Pendiente',
      desc:
        idx >= 1
          ? 'El punto ha confirmado la recepción física del paquete.'
          : 'Cuando el vendedor entregue el paquete, el punto lo validará con el código de recepción.',
      done: idx >= 1,
      act: false,
      eta: null,
    },
    {
      label: buyerShip ? 'Envío a tu dirección' : 'Listo para recoger',
      date: idx >= 2 ? fmtDate(pkg?.updated_at) : 'Pendiente',
      desc: buyerShip
        ? idx >= 2
          ? 'El punto ha gestionado el envío al comprador.'
          : 'Tras la recepción, el punto enviará el paquete a la dirección del checkout.'
        : idx >= 1 && pkg?.codigo_entrega
          ? `Recógelo en ${pointName}. Código de entrega: ${pkg.codigo_entrega}`
          : `Te avisaremos cuando puedas recogerlo en ${pointName}.`,
      done: idx >= 2 || (idx >= 1 && confirmed),
      act: idx === 1 && !confirmed,
      eta:
        buyerShip && idx === 1 && !confirmed
          ? slaOrderStepHint(MARKETPLACE_SLA.partnerOutboundToBuyer)
          : idx === 1 && !buyerShip && !confirmed
            ? slaOrderStepHint(MARKETPLACE_SLA.partnerPickupReceive)
            : null,
    },
    {
      label: 'Recepción confirmada',
      date: confirmed ? fmtDateTime(tx.buyer_confirmed_at) : 'Pendiente',
      desc: confirmed ? 'Has confirmado la recepción del disco.' : 'Confirma cuando lo tengas en tu poder.',
      done: confirmed,
      pend: !confirmed && idx >= 1,
    },
  ];
}

function paymentStateLabel(tx) {
  if (tx.buyer_confirmed_at) return 'Liberado';
  const st = String(tx.status || '').toLowerCase();
  if (st.includes('cancel')) return 'Cancelado';
  if (st.includes('refund')) return 'Reembolsado';
  return 'Retenido';
}

function listingStubFromRow(row) {
  if (!row) {
    return { id: 'x', title: 'Anuncio', artist: '—', year: null, condition: '—', price: 0, photos: [], coverUrl: null };
  }
  return {
    id: row.id,
    title: row.title || '—',
    artist: row.artist || '—',
    year: row.year,
    condition: row.condition || '—',
    price: Number(row.price),
    photos: row.photos ?? [],
    coverUrl: row.cover_url ?? null,
  };
}

function buildSteps(tx) {
  const cancelled = String(tx.status || '').toLowerCase().includes('cancel');
  const confirmed = !!tx.buyer_confirmed_at;
  const hasTrack = !!(tx.tracking_number && String(tx.tracking_number).trim());
  const st = String(tx.status || '');

  if (cancelled) {
    return [
      { label: 'Pedido cancelado', date: fmtDateTime(tx.created_at), desc: `Estado: ${st || 'cancelado'}`, done: false, act: false, pend: true },
    ];
  }

  return [
    {
      label: 'Compra registrada',
      date: fmtDateTime(tx.created_at),
      desc: 'Tu compra consta en DreamVinyl. El pago sigue el flujo de compra protegida.',
      done: true,
    },
    {
      label: 'Preparación y envío',
      date: fmtDate(tx.created_at),
      desc: hasTrack
        ? `Seguimiento: ${tx.tracking_number}`
        : 'El vendedor prepara el envío del disco a tu dirección.',
      done: hasTrack || confirmed || !['pending_payment', 'pending'].includes(String(tx.status || '').toLowerCase()),
      act: !hasTrack && !confirmed,
      eta: !hasTrack && !confirmed ? slaOrderStepHint(MARKETPLACE_SLA.directSellerShip) : null,
      showProductImage: !confirmed,
    },
    {
      label: 'En tránsito',
      date: hasTrack ? fmtDate(tx.created_at) : 'Pendiente',
      desc: hasTrack
        ? 'Puedes seguir el envío con el número indicado.'
        : 'Pendiente de que el vendedor registre el envío.',
      done: hasTrack,
      act: hasTrack && !confirmed,
      eta: hasTrack && !confirmed ? slaOrderStepHint(MARKETPLACE_SLA.directSellerShip) : null,
    },
    {
      label: 'Recepción confirmada · Pago liberado',
      date: confirmed ? fmtDateTime(tx.buyer_confirmed_at) : 'Pendiente',
      desc: confirmed ? 'Has confirmado la recepción.' : 'Confirma cuando recibas el artículo.',
      done: confirmed,
      pend: !confirmed,
    },
  ];
}

export default function PagePedido({ onBack, onNavigate, user, highlightTransactionId, onConsumedHighlight }) {
  const [loading, setLoading] = useState(!!user?.id);
  const [rows, setRows] = useState([]);
  const [listingById, setListingById] = useState({});
  const [sellerById, setSellerById] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState('');
  const [custodyByTxId, setCustodyByTxId] = useState({});
  const [verifierPartnerByTxId, setVerifierPartnerByTxId] = useState({});
  const [pickupPkgByTxId, setPickupPkgByTxId] = useState({});
  const [pickupPartnerByTxId, setPickupPartnerByTxId] = useState({});

  const load = useCallback(async () => {
    if (!user?.id) {
      setRows([]);
      setListingById({});
      setSellerById({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: txs, error: e1 } = await promiseWithTimeout(
        supabase
          .from('transactions')
          .select(
            'id, listing_id, seller_id, buyer_id, amount, status, shipping_type, shipping_cost, commission, tracking_number, created_at, buyer_confirmed_at, verifier_partner_id, pickup_partner_id, seller_to_partner_mode, buyer_partner_delivery_mode, buyer_shipping_address, seller_dropoff_tracking, seller_dropoff_status, seller_dropoff_shipped_at'
          )
          .eq('buyer_id', user.id)
          .order('created_at', { ascending: false })
          .limit(80),
        FETCH_MS,
        'Tiempo de espera al cargar tus compras.'
      );
      if (e1) {
        console.warn('[PagePedido] transactions:', e1.message);
        setRows([]);
        setListingById({});
        setSellerById({});
        return;
      }
      const list = txs ?? [];
      setRows(list);

      const lid = [...new Set(list.map(t => t.listing_id).filter(Boolean))];
      const sid = [...new Set(list.map(t => t.seller_id).filter(Boolean))];

      const [lr, pr] = await Promise.all([
        lid.length
          ? supabase
              .from('listings')
              .select('id, title, artist, condition, format, year, price, photos, cover_url')
              .in('id', lid)
          : Promise.resolve({ data: [], error: null }),
        sid.length ? supabase.from('profiles').select('id, username').in('id', sid) : Promise.resolve({ data: [], error: null }),
      ]);

      const lm = {};
      for (const r of lr.data ?? []) lm[r.id] = r;
      setListingById(lm);

      const sm = {};
      for (const r of pr.data ?? []) sm[r.id] = r;
      setSellerById(sm);

      const verifierTxs = list.filter(t => isVerifierShippingType(t.shipping_type) && t.id);
      const txIds = verifierTxs.map(t => t.id);
      if (txIds.length) {
        const { data: custodies } = await supabase
          .from('partner_store_custody')
          .select('id, transaction_id, status, delivery_pin, partner_id, updated_at')
          .in('transaction_id', txIds);
        const cMap = {};
        const partnerIds = new Set();
        for (const c of custodies ?? []) {
          if (c.transaction_id) cMap[c.transaction_id] = c;
          if (c.partner_id) partnerIds.add(c.partner_id);
        }
        setCustodyByTxId(cMap);
        if (partnerIds.size) {
          const { data: partners } = await supabase
            .from('partners')
            .select('id, company_name, city, address, maps_url')
            .in('id', [...partnerIds]);
          const pById = Object.fromEntries((partners ?? []).map(p => [p.id, p]));
          const vpMap = {};
          for (const t of verifierTxs) {
            const cid = cMap[t.id]?.partner_id || t.verifier_partner_id;
            if (cid && pById[cid]) vpMap[t.id] = pById[cid];
          }
          setVerifierPartnerByTxId(vpMap);
        } else {
          setVerifierPartnerByTxId({});
        }
      } else {
        setCustodyByTxId({});
        setVerifierPartnerByTxId({});
      }

      const pickupTxs = list.filter(t => isPickupShippingType(t.shipping_type) && t.id);
      const pickupTxIds = pickupTxs.map(t => t.id);
      if (pickupTxIds.length) {
        const { data: pkgs } = await supabase
          .from('partner_pickup_packages')
          .select('id, transaction_id, status, codigo_recepcion, codigo_entrega, partner_id, updated_at')
          .in('transaction_id', pickupTxIds);
        const pMap = {};
        const partnerIds = new Set();
        for (const p of pkgs ?? []) {
          if (p.transaction_id) pMap[p.transaction_id] = p;
          if (p.partner_id) partnerIds.add(p.partner_id);
        }
        setPickupPkgByTxId(pMap);
        if (partnerIds.size) {
          const { data: partners } = await supabase
            .from('partners')
            .select('id, company_name, city, address, maps_url')
            .in('id', [...partnerIds]);
          const pById = Object.fromEntries((partners ?? []).map(p => [p.id, p]));
          const ppMap = {};
          for (const t of pickupTxs) {
            const pid = pMap[t.id]?.partner_id || t.pickup_partner_id;
            if (pid && pById[pid]) ppMap[t.id] = pById[pid];
          }
          setPickupPartnerByTxId(ppMap);
        } else {
          setPickupPartnerByTxId({});
        }
      } else {
        setPickupPkgByTxId({});
        setPickupPartnerByTxId({});
      }
    } catch (err) {
      console.error('[PagePedido]', err);
      setRows([]);
      setListingById({});
      setSellerById({});
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedId(null);
  }, [user?.id]);

  useEffect(() => {
    if (!highlightTransactionId || loading) return;
    setSelectedId(highlightTransactionId);
    onConsumedHighlight?.();
  }, [highlightTransactionId, loading, onConsumedHighlight]);

  const confirmReceipt = async () => {
    if (!selected?.id || selected.buyer_confirmed_at) return;
    setConfirmBusy(true);
    setConfirmErr('');
    const { error } = await buyerConfirmTransactionReceipt(selected.id);
    setConfirmBusy(false);
    if (error) {
      setConfirmErr(error);
      return;
    }
    await load();
  };

  const selected = useMemo(() => rows.find(t => t.id === selectedId) ?? null, [rows, selectedId]);
  const selectedListing = selected?.listing_id ? listingById[selected.listing_id] : null;
  const selectedSeller = selected?.seller_id ? sellerById[selected.seller_id] : null;
  const stub = useMemo(() => listingStubFromRow(selectedListing), [selectedListing]);

  const shipCost = selected != null ? Number(selected.shipping_cost) || 0 : 0;
  const totalPay =
    selected != null ? (Number(selected.amount) || 0) + (Number.isFinite(shipCost) ? shipCost : 0) : 0;

  const selectedCustody = selected?.id ? custodyByTxId[selected.id] : null;
  const selectedVerifier = selected?.id ? verifierPartnerByTxId[selected.id] : null;
  const selectedPickupPkg = selected?.id ? pickupPkgByTxId[selected.id] : null;
  const selectedPickupPartner = selected?.id ? pickupPartnerByTxId[selected.id] : null;
  const selectedGalleryUrls = useMemo(
    () => listingGalleryUrls(selectedListing || stub),
    [selectedListing, stub]
  );
  const steps = selected
    ? isVerifierShippingType(selected.shipping_type)
      ? buildStepsVerifier(selected, selectedCustody, selectedVerifier)
      : isPickupShippingType(selected.shipping_type)
        ? buildStepsPickup(selected, selectedPickupPkg, selectedPickupPartner)
        : buildSteps(selected)
    : [];

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar
        onBack={selected ? () => setSelectedId(null) : onBack}
        title={selected ? 'Detalle del pedido' : 'Mis compras'}
        mini
        dark
        user={user}
        onNavigate={onNavigate}
        onSignOut={onBack}
      />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 20px 60px' }}>
        {!user?.id ? (
          <DCard style={{ padding: 16 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Inicia sesión para ver tus compras.</div>
          </DCard>
        ) : null}

        {user?.id && loading ? (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', padding: '20px 0' }}>Cargando pedidos…</div>
        ) : null}

        {user?.id && !loading && !selected && rows.length === 0 ? (
          <DCard style={{ padding: 18 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#fff', marginBottom: 6 }}>Aún no tienes compras</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
              Cuando compres un disco, el pedido aparecerá aquí con el seguimiento.
            </div>
            <button
              type="button"
              onClick={() => onNavigate && onNavigate('marketplace')}
              style={{
                marginTop: 14,
                padding: '10px 18px',
                borderRadius: 10,
                border: 'none',
                background: C.gold,
                color: '#0a0a0f',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Ir al mercado
            </button>
          </DCard>
        ) : null}

        {user?.id && !loading && !selected
          ? rows.map(tx => {
              const li = tx.listing_id ? listingById[tx.listing_id] : null;
              const se = tx.seller_id ? sellerById[tx.seller_id] : null;
              const sstub = listingStubFromRow(li);
              const sc = Number(tx.shipping_cost) || 0;
              const tot = (Number(tx.amount) || 0) + (Number.isFinite(sc) ? sc : 0);
              return (
                <DCard
                  key={tx.id}
                  style={{ padding: 12, marginBottom: 10, cursor: 'pointer' }}
                  onClick={() => setSelectedId(tx.id)}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
                      <VinylCover item={sstub} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {li?.title || 'Pedido'}
                      </div>
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', marginTop: 2 }}>
                        {se?.username ? `@${se.username}` : 'Vendedor'} · {fmtDate(tx.created_at)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.gold, marginTop: 4 }}>{formatEuro(tot)}</div>
                    </div>
                    <Badge type={tx.buyer_confirmed_at ? 'green' : 'gold'}>{tx.buyer_confirmed_at ? 'Recibido' : 'En curso'}</Badge>
                  </div>
                  <div style={{ fontSize: 10, color: C.gold, marginTop: 8, textAlign: 'right' }}>Ver seguimiento →</div>
                </DCard>
              );
            })
          : null}

        {selected ? (
          <>
            <DCard style={{ padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12, paddingBottom: 12, borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
                <OrderListingMedia listingRow={selectedListing} stub={stub} size="hero" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{selectedListing?.title || 'Pedido'}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {selectedListing?.artist || '—'} · {selectedListing?.condition || '—'} · {selectedListing?.format || '—'}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.gold, marginTop: 3 }}>
                    {formatEuro(selected.amount)}
                    {shipCost > 0
                      ? ` + ${formatEuro(shipCost)} gestión = ${formatEuro(totalPay)}`
                      : null}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  ['Vendedor', selectedSeller?.username ? `@${selectedSeller.username}` : '—'],
                  [
                    'Tipo envío',
                    isPartnerHubTransaction(selected) && selected.buyer_partner_delivery_mode
                      ? `${shippingLabel(selected.shipping_type)} · ${buyerDeliveryModeLabel(selected.buyer_partner_delivery_mode)}`
                      : shippingLabel(selected.shipping_type),
                  ],
                  ['Fecha compra', fmtDate(selected.created_at)],
                  ['Estado pago', paymentStateLabel(selected)],
                ].map(([l, v]) => (
                  <div key={l} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{l}</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: l === 'Estado pago' && v === 'Retenido' ? C.warn : '#fff' }}>{v}</div>
                  </div>
                ))}
              </div>
            </DCard>

            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 500,
                  marginBottom: 12,
                }}
              >
                Estado del pedido
              </div>
              {steps.map((st, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, marginBottom: i < steps.length - 1 ? 12 : 0 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1.5px solid ${st.done ? C.green : st.act ? C.gold : 'rgba(255,255,255,0.12)'}`,
                        background: st.done ? 'rgba(29,158,117,0.2)' : st.act ? 'rgba(200,168,75,0.2)' : 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <Icon
                        name={st.done ? 'check' : st.act ? 'arrow_r' : 'bag'}
                        size={12}
                        color={st.done ? C.green : st.act ? C.gold : 'rgba(255,255,255,0.2)'}
                      />
                    </div>
                    {i < steps.length - 1 ? <div className={`tl-line-conn${st.done ? ' done' : ''}`} /> : null}
                  </div>
                  <div style={{ flex: 1, paddingTop: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: st.pend ? 'rgba(255,255,255,0.3)' : '#fff' }}>{st.label}</div>
                    <div style={{ fontSize: 10, color: st.pend ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.35)', marginTop: 2 }}>{st.date}</div>
                    {st.desc ? (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 3, lineHeight: 1.5 }}>{st.desc}</div>
                    ) : null}
                    {st.eta ? (
                      <div
                        style={{
                          fontSize: 10,
                          color: st.act ? C.gold : 'rgba(255,255,255,0.38)',
                          marginTop: 6,
                          lineHeight: 1.45,
                          padding: '6px 8px',
                          borderRadius: 8,
                          background: st.act ? 'rgba(200,168,75,0.08)' : 'rgba(255,255,255,0.03)',
                          border: st.act ? '0.5px solid rgba(200,168,75,0.2)' : '0.5px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        {st.eta}
                      </div>
                    ) : null}
                    {st.showProductImage && selectedGalleryUrls.length ? (
                      <OrderStepProductThumbs urls={selectedGalleryUrls} />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {isPartnerHubTransaction(selected) || selected.tracking_number ? (
              <DCard style={{ padding: 14, marginBottom: 14 }}>
                <TransactionShipmentPanel
                  transaction={selected}
                  viewerRole="buyer"
                  currentUserId={user?.id}
                  onUpdated={() => void load()}
                  title="Mensajes y seguimiento de este pedido"
                />
              </DCard>
            ) : null}

            <DCard style={{ padding: '10px 12px', marginBottom: 12, background: 'rgba(29,158,117,0.06)', borderColor: 'rgba(93,202,165,0.2)' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: C.green, marginBottom: 3 }}>Compra protegida DreamVinyl</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                Revisa el artículo al recibirlo. Los plazos del seguimiento son orientativos (días laborables) y aplican a vendedores y partners según los Términos de uso.
                Si hay un problema, contacta con soporte desde el chat.
              </div>
            </DCard>

            <DCard style={{ padding: 14, marginBottom: 12, background: 'rgba(200,168,75,0.08)', borderColor: 'rgba(200,168,75,0.3)' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.gold, marginBottom: 4 }}>¿Has recibido el disco?</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: 10 }}>
                Confirma la recepción para cerrar la venta y liberar el pago al vendedor.
              </div>
              {confirmErr ? <div style={{ fontSize: 11, color: C.warn, marginBottom: 8 }}>{confirmErr}</div> : null}
              <BtnGold onClick={confirmReceipt} disabled={confirmBusy || !!selected.buyer_confirmed_at} style={{ marginBottom: 8, borderRadius: 12 }}>
                {confirmBusy ? 'Confirmando…' : selected.buyer_confirmed_at ? 'Recepción confirmada' : 'Confirmar recepción'}
              </BtnGold>
              <button
                type="button"
                onClick={() =>
                  onNavigate &&
                  onNavigate('valoraciones', { reviewTransactionId: selected.id, reviewRole: 'buyer' })
                }
                style={{
                  width: '100%',
                  padding: '9px',
                  borderRadius: 10,
                  border: '0.5px solid rgba(255,255,255,0.15)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.65)',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Valorar al vendedor
              </button>
            </DCard>

            <button
              type="button"
              onClick={() => onNavigate && onNavigate('chat')}
              style={{
                width: '100%',
                padding: '11px',
                borderRadius: 12,
                background: 'transparent',
                border: '0.5px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.6)',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Icon name="chat" size={13} color="rgba(255,255,255,0.6)" /> Ir al chat con el vendedor
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
